import { Component, OnInit, ViewChild } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Router } from '@angular/router';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { GlobalLoadingService } from '../services/global-loading.service';
import { Subscription } from 'rxjs';
import { HostListener } from '@angular/core';
import {
  BottomSheetData,
  ViewDataType,
  BottomSheetComponent,
} from '../bottom-sheet/bottom-sheet.component';
import { AuthService } from '../services/auth.service';
import {
  CommonFunctionsService,
  DialogType,
} from '../services/common-functions.service';
import { DataModelService } from '../services/data-model.service';
import {
  PointTransfer,
  Properties,
  TransferState,
  User,
} from '../services/definitions.service';

@Component({
  selector: 'app-point-receive',
  templateUrl: './point-receive.component.html',
  styleUrls: ['./point-receive.component.css'],
})
export class PointReceiveComponent implements OnInit {
  private pointTransferCollectionSub: Subscription;
  public scannerEnabled: boolean = true;
  public camInitialized: boolean = false;
  private userData: User;
  private transactionId: string = '';
  public userSpecificFinishedTransfers: PointTransfer[] = [];
  public showAskPermissionButton: boolean = false;
  public foundCameras: any[];
  private currentCamIx: number = 0;
  private readonly KEY_CAM_IX: string = 'last_camera_ix';
  private scanAllowed: boolean = true;
  private transactionTimeout: any = null;

  @ViewChild('scanner', { static: false })
  scanner: ZXingScannerComponent;

  constructor(
    private commonFunctions: CommonFunctionsService,
    private firestore: AngularFirestore,
    private dataModelService: DataModelService,
    private authService: AuthService,
    private bottomSheet: MatBottomSheet,
    private globalLoading: GlobalLoadingService,
    private router: Router
  ) {
    // Assign the data to the data source for the table to render
    this.commonFunctions.registerEventEmitterHandlerUserData((data: User) => {
      this.userData = data;
    });
    this.scannerEnabled = true;
  }

  ngOnInit(): void {
    this.subscribeOnCollectionChange();
  }

  ngOnDestroy() {
    this.unsubscribeEventEmitterListener();
    this.scannerEnabled = false;
    this.scanner?.ngOnDestroy();
  }

  ngAfterViewInit() {
    this.scanner.reset();
    this.scanner.restart();
    this.askForPermission();
  }

  public scanSuccess(result: string) {}

  public scanFailure(event) {}

  public scanError(err: any) {}

  public scanComplete(val: any) {
    if (val && this.scanAllowed && !this.transactionId) {
      this.scanAllowed = false;
      this.disableCamera();
      var data = val.text;
      if (data) {
        var splitted = data.split(';');
        //check consistency
        if (splitted.length == 4) {
          var senderUserId = splitted[0];
          var senderUserName = splitted[1];
          var amountPoints = splitted[2];
          var comment = splitted[3];
          var receiverUserId = this.authService.userId;
          var receiverUserName = this.commonFunctions.getPrettyPrintedUserName(
            this.userData
          );

          // check that sender and receiver are not identical
          if (senderUserId == receiverUserId) {
            this.commonFunctions.showErrorToast(
              'Sender und Empfänger dürfen nicht identisch sein'
            );
            return;
          }

          //create a new points transfer entry
          this.createPointsEntry(
            senderUserId,
            senderUserName,
            receiverUserId,
            receiverUserName,
            amountPoints,
            comment,
            TransferState.PENDING
          );
        } else {
          this.commonFunctions.showErrorToast('Ungültiger QR-Code');
        }
      } else {
        this.commonFunctions.showErrorToast('Ungültiger QR-Code');
      }
    }
  }

  //Creates a new entry in the point transfer collection
  private createPointsEntry(
    senderUserId: string,
    senderUserName: string,
    receiverUserId: string,
    receiverUserName: string,
    amountPoints: number,
    comment: string,
    state: string
  ) {
    this.dataModelService
      .createPointTransferEntry(
        senderUserId,
        senderUserName,
        receiverUserId,
        receiverUserName,
        amountPoints,
        comment,
        state
      )
      .then(
        (res) => {
          if (res) {
            this.transactionId = res;
            this.startTransactionTimeout();
            this.commonFunctions.showSuccessToast(
              'QR-Code erkannt. Warte auf Bestätigung vom Sender...'
            );
          } else {
            this.commonFunctions.showErrorToast(
              'Transfer-Punkte konnten nicht gespeichert werden.'
            );
            this.resetTransaction();
          }
        },
        (rejected) => {
          this.commonFunctions.showErrorToast(
            'Transfer-Punkte konnten nicht gespeichert werden: ' + rejected
          );
          this.resetTransaction();
        }
      )
      .catch((error) => {
        this.commonFunctions.showErrorToast(
          'Transfer-Punkte konnten nicht gespeichert werden: ' + error
        );
        this.resetTransaction();
      });
  }

  //Resets the transaction state (camera stays off until user re-enables)
  private resetTransaction() {
    if (this.transactionTimeout) {
      clearTimeout(this.transactionTimeout);
      this.transactionTimeout = null;
    }
    this.transactionId = '';
    this.scanAllowed = false;
    this.scannerEnabled = false;
  }

  //Disables the camera
  private disableCamera() {
    this.scannerEnabled = false;
  }

  //Re-enables the camera and allows scanning again
  public enableCamera() {
    this.scannerEnabled = true;
    this.scanAllowed = true;
  }

  //Starts a timeout that declines the transaction if the sender doesn't respond in time
  private startTransactionTimeout() {
    this.transactionTimeout = setTimeout(() => {
      if (this.transactionId) {
        var errMsg = 'Konnte status nicht in der Datenbank speichern';
        this.dataModelService
          .updateTransferEntryState(this.transactionId, TransferState.DECLINED)
          .then(
            () => {},
            () => {
              this.commonFunctions.showErrorToast(errMsg);
            }
          )
          .catch(() => {
            this.commonFunctions.showErrorToast(errMsg);
          });
        this.commonFunctions.showErrorToast('Transaktion abgelaufen');
        this.resetTransaction();
      }
    }, 30000);
  }

  //Listens to changes on the point transfer collection
  private subscribeOnCollectionChange() {
    var thisUsersId = this.authService.userId;
    this.pointTransferCollectionSub = this.firestore
      .collection(PointTransfer())
      .valueChanges({ idField: 'id' })
      .subscribe(
        (data: any[]) => {
          if (data) {
            var allTransfers: PointTransfer[] = data;

            //get all finished (for showing history)
            this.userSpecificFinishedTransfers = allTransfers.filter(
              (x) =>
                x.receiverUserId == thisUsersId &&
                x.state == TransferState.FINISHED
            );

            //get all with this id
            var allToCheck = allTransfers.filter(
              (x) => x.id == this.transactionId
            );

            //if we have an active transaction but the entry was deleted (e.g. by sender cleanup), reset
            if (this.transactionId && allToCheck.length == 0) {
              this.commonFunctions.showErrorToast(
                'Transaktion wurde abgebrochen'
              );
              this.resetTransaction();
            }

            allToCheck.forEach((element) => {
              //checke ob der state auf finished oder declined ist
              if (element.id == this.transactionId) {
                if (element.state == TransferState.FINISHED) {
                  this.commonFunctions.showSuccessToast(
                    'Punkte-Transfer erfolgreich'
                  );
                  this.resetTransaction();
                  this.router.navigate(['/home']);
                } else if (element.state == TransferState.DECLINED) {
                  this.commonFunctions.showErrorToast(
                    'Transaktion vom Sender abgelehnt'
                  );
                  this.resetTransaction();
                }
              }
            });
          }
        },
        (error) => {
          this.commonFunctions.showErrorToast(error, 7000);
        }
      );
  }

  private unsubscribeEventEmitterListener() {
    if (this.pointTransferCollectionSub)
      this.pointTransferCollectionSub.unsubscribe();
  }

  // Shows the points history in the bottom sheet
  public showPointsHistory() {
    //sort data
    this.userSpecificFinishedTransfers.sort((a, b) =>
      a[Properties.Timestamp] < b[Properties.Timestamp] ? 1 : -1
    );
    let data = new BottomSheetData(
      this.userSpecificFinishedTransfers,
      ViewDataType.POINT_TRANSFER_RECEIVER
    );
    this.bottomSheet.open(BottomSheetComponent, {
      data: data,
    });
  }

  // asks for the permission to use the camera
  public async askForPermission() {
    var failed: boolean = false;
    var error = '';
    //Ask for permission
    await navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(function (stream) {
        /* use the stream */
      })
      .catch(function (err) {
        /* handle the error */
        failed = true;
        error = err;
      });
    if (failed) {
      this.commonFunctions.showErrorToast(
        'Keine Berechtigung zur Nutzung der Kamera'
      );
    }
  }

  //handles the click on 'Kein Kamerabild sichtbar?' icon-button
  public showInfoDialog() {
    this.commonFunctions.openDialog(
      'Info',
      'Versuche die Kamera zu wechseln. Ein reload der Seite kann in manchen Fällen auch helfen. Solltest du ein IPhone besitzen, so funktioniert die Kamera erst ab der iOS Version 14.3.',
      DialogType.INFO
    );
  }

  //Handle the event which includes all available cameras
  public camerasFoundHandler(event) {
    this.foundCameras = event;
    this.handleCamInit();
  }

  //Handles the initiating phase of the camera
  private async handleCamInit() {
    if (!this.camInitialized) {
      this.camInitialized = true;
      var lastIx = localStorage.getItem(this.KEY_CAM_IX);
      if (lastIx) {
        var ixNum = parseInt(lastIx);

        await this.delay(3000);
        // this.commonFunctions.showSuccessToast(
        //   'Restoring cam settings to: ' + this.foundCameras[ixNum].label
        // );
        this.scanner.reset();
        this.scanner.device = this.foundCameras[ixNum];
        this.globalLoading.stopLoading();
      } else {
        this.globalLoading.stopLoading();
      }
    }
  }

  //Wait a specific delay
  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  //Handle the event when no camera is available
  public camerasNotFoundHandler(event) {
    this.commonFunctions.showErrorToast('Keine Kamera vorhanden');
    this.globalLoading.stopLoading();
  }

  //handles camera device change event
  public deviceHasChanged(event) {
    // console.log('Device has changed: ' + event?.label);
  }

  //changes the camera device
  public changeCamera() {
    if (this.foundCameras) {
      var ix = this.currentCamIx + 1;
      if (ix > this.foundCameras.length - 1) {
        ix = 0;
      }
      this.currentCamIx = ix;
      this.scanner.reset();
      this.scanner.device = this.foundCameras[ix];
      //save last selection
      localStorage.setItem(this.KEY_CAM_IX, ix.toString());
    }
  }

  //Listens on back button click
  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    //auf declined setzen
    if (this.transactionId) {
      var errMsg = 'Konnte status nicht in der Datenbank speichern';
      this.dataModelService
        .updateTransferEntryState(this.transactionId, TransferState.DECLINED)
        .then(
          () => {},
          () => {
            this.commonFunctions.showErrorToast(errMsg);
          }
        )
        .catch(() => {
          this.commonFunctions.showErrorToast(errMsg);
        });
      this.resetTransaction();
    }
  }
}
